import { isMock, db } from './firebase'
import { logger } from './logger'
import type { Message, ConversationSummary, User } from '../types/message'
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  getDoc,
  orderBy,
  serverTimestamp,
  Timestamp,
  DocumentData,
  limit,
  startAfter,
  writeBatch,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore'

// Firestore collection names
const CONVERSATIONS_COLLECTION = 'conversations'
const MESSAGES_COLLECTION = 'messages'
const USERS_COLLECTION = 'users'
const CONVERSATION_PARTICIPANTS_COLLECTION = 'conversationParticipants'

// Mock data generation (fallback)
const generateMockUsers = (): User[] => {
  return [
    {
      id: 'user-1',
      name: 'João Silva',
      email: 'joao@example.com',
      avatar: 'https://i.pravatar.cc/150?img=8',
      isOnline: true,
      lastSeen: new Date(),
    },
    {
      id: 'user-2',
      name: 'Maria Souza',
      email: 'maria@example.com',
      avatar: 'https://i.pravatar.cc/150?img=9',
      isOnline: false,
      lastSeen: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
    },
    {
      id: 'user-3',
      name: 'Carlos Mendes',
      email: 'carlos@example.com',
      avatar: 'https://i.pravatar.cc/150?img=10',
      isOnline: true,
      lastSeen: new Date(),
    },
    {
      id: 'user-4',
      name: 'Ana Costa',
      email: 'ana@example.com',
      avatar: 'https://i.pravatar.cc/150?img=11',
      isOnline: false,
      lastSeen: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
    },
    {
      id: 'user-5',
      name: 'Pedro Alves',
      email: 'pedro@example.com',
      avatar: 'https://i.pravatar.cc/150?img=12',
      isOnline: true,
      lastSeen: new Date(),
    },
  ]
}

const generateMockMessages = (conversationId: string, userId: string, otherUserId: string): Message[] => {
  const now = new Date()
  return [
    {
      id: 'msg-1',
      conversationId,
      senderId: otherUserId,
      receiverId: userId,
      content: 'Olá! Vi seu anúncio do apartamento. Ainda está disponível?',
      timestamp: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
      read: true,
      type: 'text',
    },
    {
      id: 'msg-2',
      conversationId,
      senderId: userId,
      receiverId: otherUserId,
      content: 'Sim, ainda está disponível! Você gostaria de agendar uma visita?',
      timestamp: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
      read: true,
      type: 'text',
    },
    {
      id: 'msg-3',
      conversationId,
      senderId: otherUserId,
      receiverId: userId,
      content: 'Perfeito! Você tem disponibilidade no sábado à tarde?',
      timestamp: new Date(now.getTime() - 12 * 60 * 60 * 1000), // 12 hours ago
      read: true,
      type: 'text',
    },
    {
      id: 'msg-4',
      conversationId,
      senderId: userId,
      receiverId: otherUserId,
      content: 'Sim, posso às 15h. Envio o endereço por aqui.',
      timestamp: new Date(now.getTime() - 6 * 60 * 60 * 1000), // 6 hours ago
      read: true,
      type: 'text',
    },
    {
      id: 'msg-5',
      conversationId,
      senderId: otherUserId,
      receiverId: userId,
      content: 'Ótimo! Levo minha esposa para ver também. Pode enviar algumas fotos adicionais?',
      timestamp: new Date(now.getTime() - 2 * 60 * 60 * 1000), // 2 hours ago
      read: false,
      type: 'text',
    },
    {
      id: 'msg-6',
      conversationId,
      senderId: userId,
      receiverId: otherUserId,
      content: 'Claro! Aqui estão mais fotos do apartamento.',
      timestamp: new Date(now.getTime() - 1 * 60 * 60 * 1000), // 1 hour ago
      read: false,
      type: 'image',
      metadata: {
        imageUrl: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800',
        imageDimensions: { width: 800, height: 600 },
      },
    },
  ]
}

const generateMockConversations = (userId: string): ConversationSummary[] => {
  const mockUsers = generateMockUsers()
  const otherUsers = mockUsers.filter(user => user.id !== userId)
  
  return otherUsers.map((user, index) => {
    const conversationId = `conv-${index + 1}`
    const messages = generateMockMessages(conversationId, userId, user.id)
    const lastMessage = messages[messages.length - 1]
    
    return {
      id: conversationId,
      otherUser: {
        id: user.id,
        name: user.name,
        avatar: user.avatar,
        isOnline: user.isOnline,
      },
      lastMessage: {
        content: lastMessage.content,
        timestamp: lastMessage.timestamp,
        senderId: lastMessage.senderId,
        read: lastMessage.read,
      },
      unreadCount: messages.filter(m => !m.read && m.senderId !== userId).length,
      listingInfo: index % 2 === 0 ? {
        id: `listing-${index + 1}`,
        title: index === 0 ? 'Apartamento em Miami' : 'Toyota Camry 2022',
        price: index === 0 ? 350000 : 25000,
        image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800',
      } : undefined,
      updatedAt: lastMessage.timestamp,
    }
  })
}

// Helper functions to convert Firestore data
const firestoreToMessage = (docId: string, data: DocumentData): Message => {
  const convertTimestamp = (timestamp: unknown) => 
    timestamp instanceof Timestamp
      ? timestamp.toDate() 
      : new Date()

  return {
    id: docId,
    conversationId: data.conversationId || '',
    senderId: data.senderId || '',
    receiverId: data.receiverId || '',
    content: data.content || '',
    timestamp: convertTimestamp(data.timestamp),
    read: data.read || false,
    type: data.type || 'text',
    metadata: data.metadata === null ? undefined : data.metadata,
    threadId: data.threadId === null ? undefined : data.threadId,
    parentMessageId: data.parentMessageId === null ? undefined : data.parentMessageId,
    threadType: data.threadType === null ? undefined : data.threadType,
  }
}

const firestoreToConversationSummary = async (conversationId: string, data: DocumentData, currentUserId: string): Promise<ConversationSummary | null> => {
  try {
    // Get other participant
    const participants = data.participants || []
    const otherUserId = participants.find((id: string) => id !== currentUserId)
    
    if (!otherUserId) return null

    // Get user data
    let otherUser: User | null = null
    if (isMock) {
      const mockUsers = generateMockUsers()
      otherUser = mockUsers.find(u => u.id === otherUserId) || null
    } else {
      const userRef = doc(db, USERS_COLLECTION, otherUserId)
      const userSnap = await getDoc(userRef)
      if (userSnap.exists()) {
        const userData = userSnap.data()
        otherUser = {
          id: userSnap.id,
          name: userData.name || userData.displayName || 'Usuário',
          email: userData.email || '',
          avatar: userData.photoURL || userData.avatar,
          isOnline: userData.isOnline || false,
          lastSeen: userData.lastSeen ? userData.lastSeen.toDate() : undefined,
        }
      }
    }

    if (!otherUser) return null

    // Get last message
    const messagesRef = collection(db, MESSAGES_COLLECTION)
    const q = query(
      messagesRef,
      where('conversationId', '==', conversationId),
      orderBy('timestamp', 'desc'),
      limit(1)
    )
    let lastMessage = null
    let unreadCount = 0
    
    if (isMock) {
      const mockMessages = generateMockMessages(conversationId, currentUserId, otherUserId)
      const lastMsg = mockMessages[mockMessages.length - 1]
      lastMessage = {
        content: lastMsg.content,
        timestamp: lastMsg.timestamp,
        senderId: lastMsg.senderId,
        read: lastMsg.read,
      }
      unreadCount = mockMessages.filter(m => !m.read && m.senderId !== currentUserId).length
    } else {
      const messagesSnapshot = await getDocs(q)
      if (!messagesSnapshot.empty) {
        const lastMsgDoc = messagesSnapshot.docs[0]
        const lastMsgData = lastMsgDoc.data()
        lastMessage = {
          content: lastMsgData.content,
          timestamp: lastMsgData.timestamp.toDate(),
          senderId: lastMsgData.senderId,
          read: lastMsgData.read,
        }
        
        // Count unread messages
        const unreadQuery = query(
          messagesRef,
          where('conversationId', '==', conversationId),
          where('receiverId', '==', currentUserId),
          where('read', '==', false)
        )
        const unreadSnapshot = await getDocs(unreadQuery)
        unreadCount = unreadSnapshot.size
      }
    }

    if (!lastMessage) return null

    return {
      id: conversationId,
      otherUser: {
        id: otherUser.id,
        name: otherUser.name,
        avatar: otherUser.avatar,
        isOnline: otherUser.isOnline,
      },
      lastMessage,
      unreadCount,
      listingInfo: data.listingId ? {
        id: data.listingId,
        title: data.listingTitle || '',
        price: data.listingPrice || 0,
        image: data.listingImage,
      } : undefined,
      updatedAt: data.updatedAt ? data.updatedAt.toDate() : new Date(),
    }
  } catch (error) {
    logger.error('Error converting conversation:', error)
    return null
  }
}

// Service implementation
export const messageService = {
  // Get conversations for a user
  async getConversations(userId: string): Promise<ConversationSummary[]> {
    if (isMock) {
      return generateMockConversations(userId)
    }
    try {
      const conversationsRef = collection(db, CONVERSATIONS_COLLECTION)
      // Query conversations where userId is in participants array
      const q = query(conversationsRef, where('participants', 'array-contains', userId))
      const querySnapshot = await getDocs(q)
      const conversations: ConversationSummary[] = []
      for (const conversationDoc of querySnapshot.docs) {
        // Check if conversation is archived or deleted for this user
        const participantRef = doc(db, CONVERSATION_PARTICIPANTS_COLLECTION, `${userId}_${conversationDoc.id}`)
        const participantSnap = await getDoc(participantRef)
        if (participantSnap.exists()) {
          const participantData = participantSnap.data() as { archived?: boolean; deleted?: boolean }
          if (participantData.archived || participantData.deleted) {
            continue
          }
        }
        const conversation = await firestoreToConversationSummary(conversationDoc.id, conversationDoc.data(), userId)
        if (conversation) {
          conversations.push(conversation)
        }
      }
      // Sort by updatedAt descending
      conversations.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
      return conversations
    } catch (error) {
      logger.error('Error fetching conversations from Firestore:', error)
      return []
    }
  },

  // Get messages for a conversation with pagination support
  async getMessages(
    conversationId: string, 
    userId: string, 
    options?: { limit?: number; startAfter?: Date }
  ): Promise<Message[]> {
    const limitCount = options?.limit || 50
    const startAfterDate = options?.startAfter
    
    if (isMock) {
      // For mock, generate messages for a specific conversation
      const otherUserId = 'user-2' // Default for mock
      const allMessages = generateMockMessages(conversationId, userId, otherUserId)
      // Sort by timestamp ascending (already sorted in generation)
      if (startAfterDate) {
        const startIndex = allMessages.findIndex(msg => msg.timestamp > startAfterDate)
        return startIndex >= 0 ? allMessages.slice(startIndex, startIndex + limitCount) : []
      }
      return allMessages.slice(0, limitCount)
    }
    try {
      const messagesRef = collection(db, MESSAGES_COLLECTION)
      let q = query(
        messagesRef,
        where('conversationId', '==', conversationId),
        orderBy('timestamp', 'asc')
      )
      if (startAfterDate) {
        q = query(q, startAfter(Timestamp.fromDate(startAfterDate)))
      }
      q = query(q, limit(limitCount))
      const querySnapshot = await getDocs(q)
      const messages: Message[] = []
      querySnapshot.forEach((doc) => {
        messages.push(firestoreToMessage(doc.id, doc.data()))
      })
      return messages
    } catch (error) {
      logger.error('Error fetching messages from Firestore:', error)
      return []
    }
  },

  // Send a new message
  async sendMessage(
    conversationId: string | null,
    senderId: string,
    receiverId: string,
    content: string,
    type: Message['type'] = 'text',
    metadata?: Message['metadata'],
    threadType?: Message['threadType'],
    parentMessageId?: string,
    threadId?: string
  ): Promise<Message> {
    logger.log('sendMessage: isMock =', isMock, 'conversationId:', conversationId, 'senderId:', senderId, 'receiverId:', receiverId)
    if (isMock) {
       const newMessage: Message = {
        id: `msg-${Date.now()}`,
        conversationId: conversationId || `conv-${Date.now()}`,
        senderId,
        receiverId,
        content,
        timestamp: new Date(),
        read: false,
        type,
        metadata,
        threadType,
        parentMessageId,
        threadId,
      }
      logger.log('Mock: sending message', newMessage)
      return newMessage
    }
    try {
      let finalConversationId = conversationId
      const batch = writeBatch(db)
      
      // If no conversationId, create a new conversation
      if (!finalConversationId) {
        finalConversationId = `conv_${Date.now()}_${senderId}_${receiverId}`
        const conversationRef = doc(db, CONVERSATIONS_COLLECTION, finalConversationId)
        batch.set(conversationRef, {
          participants: [senderId, receiverId],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
      }
      
      // Add message
      const messagesRef = collection(db, MESSAGES_COLLECTION)
      const messageRef = doc(messagesRef)
      const timestamp = serverTimestamp()
        const messageData: DocumentData = {
        conversationId: finalConversationId,
        senderId,
        receiverId,
        content,
        timestamp,
        read: false,
        type,
      }
      logger.log('sendMessage metadata:', metadata, 'threadType:', threadType, 'parentMessageId:', parentMessageId, 'threadId:', threadId)
      if (metadata !== undefined) {
        const cleanedMetadata = Object.fromEntries(
          Object.entries(metadata).filter(([, v]) => v !== undefined)
        )
        if (Object.keys(cleanedMetadata).length > 0) {
          messageData.metadata = cleanedMetadata
        }
      }
      if (threadType !== undefined) messageData.threadType = threadType
      if (parentMessageId !== undefined) messageData.parentMessageId = parentMessageId
      if (threadId !== undefined) messageData.threadId = threadId
      batch.set(messageRef, messageData)
      
      // Update conversation's updatedAt
      const conversationRef = doc(db, CONVERSATIONS_COLLECTION, finalConversationId!)
      batch.update(conversationRef, {
        updatedAt: serverTimestamp(),
      })
      
      await batch.commit()
      
      // Return the message object (Firestore will generate timestamp, we approximate)
      const newMessage: Message = {
        id: messageRef.id,
        conversationId: finalConversationId!,
        senderId,
        receiverId,
        content,
        timestamp: new Date(), // Approximate; real timestamp set by server
        read: false,
        type,
        metadata,
        threadType,
        parentMessageId,
        threadId,
      }
      return newMessage
     } catch (error) {
      logger.error('Error sending message to Firestore:', error)
      logger.error('Error details:', error instanceof Error ? error.message : error)
      throw error
    }
  },

  // Mark messages as read
  async markAsRead(conversationId: string, userId: string): Promise<void> {
    if (isMock) {
      logger.log(`Mock: marking messages as read for conversation ${conversationId} by user ${userId}`)
      return
    }
    try {
      const messagesRef = collection(db, MESSAGES_COLLECTION)
      const q = query(
        messagesRef,
        where('conversationId', '==', conversationId),
        where('receiverId', '==', userId),
        where('read', '==', false)
      )
      const querySnapshot = await getDocs(q)
      const batch = writeBatch(db)
      querySnapshot.forEach((doc) => {
        batch.update(doc.ref, { read: true })
      })
      if (querySnapshot.size > 0) {
        await batch.commit()
      }
    } catch (error) {
      logger.error('Error marking messages as read in Firestore:', error)
    }
  },

  // Delete a message
  async deleteMessage(messageId: string): Promise<void> {
    if (isMock) {
      logger.log(`Mock: deleting message ${messageId}`)
      return
    }
    try {
      const messageRef = doc(db, MESSAGES_COLLECTION, messageId)
      await deleteDoc(messageRef)
    } catch (error) {
      logger.error('Error deleting message from Firestore:', error)
    }
  },

  // Archive a conversation
  async archiveConversation(conversationId: string, userId: string): Promise<void> {
    if (isMock) {
      logger.log(`Mock: archiving conversation ${conversationId} for user ${userId}`)
      return
    }
    try {
      const participantRef = doc(db, CONVERSATION_PARTICIPANTS_COLLECTION, `${userId}_${conversationId}`)
      await setDoc(participantRef, {
        userId,
        conversationId,
        archived: true,
        archivedAt: serverTimestamp(),
      }, { merge: true })
    } catch (error) {
      logger.error('Error archiving conversation in Firestore:', error)
    }
  },

  // Delete a conversation (soft delete)
  async deleteConversation(conversationId: string, userId: string): Promise<void> {
    if (isMock) {
      logger.log(`Mock: deleting conversation ${conversationId} for user ${userId}`)
      return
    }
    try {
      const participantRef = doc(db, CONVERSATION_PARTICIPANTS_COLLECTION, `${userId}_${conversationId}`)
      await setDoc(participantRef, {
        userId,
        conversationId,
        deleted: true,
        deletedAt: serverTimestamp(),
      }, { merge: true })
    } catch (error) {
      logger.error('Error deleting conversation in Firestore:', error)
    }
  },

  // Block a user
  async blockUser(blockerId: string, blockedId: string): Promise<void> {
    if (isMock) {
      logger.log(`Mock: user ${blockerId} blocking user ${blockedId}`)
      return
    }
    try {
      const blockRef = doc(db, 'blocks', `${blockerId}_${blockedId}`)
      await setDoc(blockRef, {
        blockerId,
        blockedId,
        blockedAt: serverTimestamp(),
      }, { merge: true })
    } catch (error) {
      logger.error('Error blocking user in Firestore:', error)
    }
  },

  // Get user by ID
  async getUser(userId: string): Promise<User | null> {
    if (isMock) {
      const mockUsers = generateMockUsers()
      return mockUsers.find(user => user.id === userId) || null
    }
    try {
      const userRef = doc(db, USERS_COLLECTION, userId)
      const userSnap = await getDoc(userRef)
      if (userSnap.exists()) {
        const data = userSnap.data()
        return {
          id: userSnap.id,
          name: data.name || data.displayName || 'Usuário',
          email: data.email || '',
          avatar: data.photoURL || data.avatar,
          isOnline: data.isOnline || false,
          lastSeen: data.lastSeen ? data.lastSeen.toDate() : undefined,
        }
      }
      return null
    } catch (error) {
      logger.error('Error fetching user from Firestore:', error)
      return null
    }
  },

  // Search users
  async searchUsers(searchQuery: string): Promise<User[]> {
    if (isMock) {
      const mockUsers = generateMockUsers()
      return mockUsers.filter(user => 
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }
    try {
      const usersRef = collection(db, USERS_COLLECTION)
      // Firestore doesn't support full text search; fetch first 50 users and filter client-side
      const q = query(usersRef, limit(50))
      const querySnapshot = await getDocs(q)
      const users: User[] = []
      const lowerQuery = searchQuery.toLowerCase()
      querySnapshot.forEach((doc) => {
        const data = doc.data()
        const name = data.name || data.displayName || ''
        const email = data.email || ''
        if (name.toLowerCase().includes(lowerQuery) || email.toLowerCase().includes(lowerQuery)) {
          users.push({
            id: doc.id,
            name,
            email,
            avatar: data.photoURL || data.avatar,
            isOnline: data.isOnline || false,
            lastSeen: data.lastSeen ? data.lastSeen.toDate() : undefined,
          })
        }
      })
      return users
    } catch (error) {
      logger.error('Error searching users in Firestore:', error)
      return []
    }
  },

  // Find or create conversation for a listing
  async findOrCreateListingConversation(
    userId: string,
    sellerId: string,
    listingId: string,
    listingTitle: string,
    listingPrice: number,
    listingImage?: string
  ): Promise<string> {
    if (isMock) {
      logger.log(`Mock: finding/creating conversation for listing "${listingTitle}" (${listingId}) price ${listingPrice} image ${listingImage || 'none'} between ${userId} and ${sellerId}`)
      // Generate a deterministic conversation ID based on participants and listing
      const sortedIds = [userId, sellerId].sort()
      const conversationId = `conv-${sortedIds[0]}-${sortedIds[1]}-${listingId}`
      return conversationId
    }
    try {
      const conversationsRef = collection(db, CONVERSATIONS_COLLECTION)
      // Query conversations with both participants and matching listingId
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', userId),
        where('listingId', '==', listingId)
      )
      const querySnapshot = await getDocs(q)
      // Filter to ensure sellerId is also a participant (Firestore doesn't support multiple array-contains)
      let existingConv = null
      for (const doc of querySnapshot.docs) {
        const data = doc.data()
        if (data.participants && data.participants.includes(sellerId)) {
          existingConv = doc
          break
        }
      }
      if (existingConv) {
        return existingConv.id
      }
      // Create new conversation
      const newConvId = `conv_${Date.now()}_${userId}_${sellerId}_${listingId}`
      const conversationRef = doc(db, CONVERSATIONS_COLLECTION, newConvId)
        const conversationData: DocumentData = {
        participants: [userId, sellerId],
        listingId,
        listingTitle,
        listingPrice,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
      if (listingImage !== undefined) conversationData.listingImage = listingImage
      await setDoc(conversationRef, conversationData)
      return newConvId
    } catch (error) {
      logger.error('Error finding/creating listing conversation in Firestore:', error)
      // Fallback to deterministic ID
      const sortedIds = [userId, sellerId].sort()
      return `conv-${sortedIds[0]}-${sortedIds[1]}-${listingId}`
     }
   },

  // Subscribe to conversations updates
  subscribeToConversations(
    userId: string,
    callback: (conversations: ConversationSummary[]) => void
  ): Unsubscribe {
    if (isMock) {
      logger.log('Mock: subscribing to conversations')
      // Mock implementation - simulate updates every 10 seconds
      const interval = setInterval(async () => {
        const conversations = await this.getConversations(userId)
        callback(conversations)
      }, 10000)
      // Return unsubscribe function
      return () => clearInterval(interval)
    }
    
    try {
      const conversationsRef = collection(db, CONVERSATIONS_COLLECTION)
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', userId)
      )
      
      const unsubscribe = onSnapshot(q, async (snapshot) => {
        const conversations: ConversationSummary[] = []
        for (const doc of snapshot.docs) {
          const conversation = await firestoreToConversationSummary(doc.id, doc.data(), userId)
          if (conversation) {
            conversations.push(conversation)
          }
        }
        // Sort by updatedAt descending
        conversations.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
        callback(conversations)
      })
      
      return unsubscribe
    } catch (error) {
      logger.error('Error subscribing to conversations:', error)
      // Return empty unsubscribe function
      return () => {}
    }
  },

  // Subscribe to messages updates in a conversation
  subscribeToMessages(
    conversationId: string,
    callback: (messages: Message[]) => void
  ): Unsubscribe {
    if (isMock) {
      logger.log(`Mock: subscribing to messages for conversation ${conversationId}`)
      // Mock implementation
      const interval = setInterval(async () => {
        const messages = await this.getMessages(conversationId, '', {})
        callback(messages)
      }, 5000)
      return () => clearInterval(interval)
    }
    
    try {
      const messagesRef = collection(db, MESSAGES_COLLECTION)
      const q = query(
        messagesRef,
        where('conversationId', '==', conversationId),
        orderBy('timestamp', 'asc')
      )
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const messages: Message[] = []
        snapshot.forEach((doc) => {
          messages.push(firestoreToMessage(doc.id, doc.data()))
        })
        callback(messages)
      })
      
      return unsubscribe
    } catch (error) {
      logger.error('Error subscribing to messages:', error)
      return () => {}
    }
  },

  // Get email threads (post replies)
  async getEmailThreads(userId: string): Promise<Message[]> {
    if (isMock) {
      logger.log('Mock: getting email threads')
      // Generate mock email threads
      const now = new Date()
      return [
        {
          id: 'email-1',
          conversationId: 'conv-email-1',
          senderId: 'system',
          receiverId: userId,
          content: 'Sua resposta no post "Alguém conhece um bom corretor de imóveis em Orlando?" foi curtida por 5 pessoas.',
          timestamp: new Date(now.getTime() - 2 * 60 * 60 * 1000),
          read: false,
          type: 'text',
          threadType: 'post_reply',
          threadId: 'post-1',
        },
        {
          id: 'email-2',
          conversationId: 'conv-email-2',
          senderId: 'system',
          receiverId: userId,
          content: 'Novo comentário no seu post "Dicas para primeiro aluguel nos EUA".',
          timestamp: new Date(now.getTime() - 5 * 60 * 60 * 1000),
          read: true,
          type: 'text',
          threadType: 'post_reply',
          threadId: 'post-2',
        },
        {
          id: 'email-3',
          conversationId: 'conv-email-3',
          senderId: 'system',
          receiverId: userId,
          content: 'Seu comentário foi respondido por Lucas Santos.',
          timestamp: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
          read: false,
          type: 'text',
          threadType: 'post_reply',
          threadId: 'comment-1',
        },
      ]
    }
    
    try {
      const messagesRef = collection(db, MESSAGES_COLLECTION)
      const q = query(
        messagesRef,
        where('receiverId', '==', userId),
        where('threadType', '==', 'post_reply'),
        orderBy('timestamp', 'desc'),
        limit(50)
      )
      const querySnapshot = await getDocs(q)
      const messages: Message[] = []
      querySnapshot.forEach((doc) => {
        messages.push(firestoreToMessage(doc.id, doc.data()))
      })
      return messages
    } catch (error) {
      logger.error('Error getting email threads from Firestore:', error)
      return []
    }
  },

  // Subscribe to email threads updates
  subscribeToEmailThreads(
    userId: string,
    callback: (messages: Message[]) => void
  ): Unsubscribe {
    if (isMock) {
      logger.log('Mock: subscribing to email threads')
      const interval = setInterval(async () => {
        const threads = await this.getEmailThreads(userId)
        callback(threads)
      }, 10000)
      return () => clearInterval(interval)
    }
    
    try {
      const messagesRef = collection(db, MESSAGES_COLLECTION)
      const q = query(
        messagesRef,
        where('receiverId', '==', userId),
        where('threadType', '==', 'post_reply'),
        orderBy('timestamp', 'desc'),
        limit(50)
      )
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const messages: Message[] = []
        snapshot.forEach((doc) => {
          messages.push(firestoreToMessage(doc.id, doc.data()))
        })
        callback(messages)
      })
      
      return unsubscribe
    } catch (error) {
      logger.error('Error subscribing to email threads:', error)
      return () => {}
    }
  },
}